import React, { useCallback, useEffect, useState } from 'react';
import './App.css';
import { BrowserRouter as Router, useLocation, useNavigate } from 'react-router-dom';
import { ALL_FORMS_PATH, EDIT_FORM_PATH, LANGUAGE_ITEM_NAME } from './consts/consts';
import ScrollToTop from './utils/ScrollToTop';
import AppRoutes from './components/AppRoutes';
import { DEFAULT_LANG, isRTL, Languages, useLanguage } from './LanguageContext';
import { getTranslation } from './consts/translations';
import { FormMetadata, FormTemplate, Question, StringsFile } from './types';
import {
  getAllQuestions,
  getFormStructure,
  getFormStrings,
  getFormsMetadata,
  getFormTemplate,
} from './api/AuthorizationApi';
import { normalizeFormLanguageCodes } from './utils/languageMapping';
import { logClientError } from './utils/logClientError';
import {
  loadSplitTemplateArtifacts,
  mergedFormTemplateFromLoadResult,
} from './utils/templateLoader';

/** Form id from `/edit-form/{id}`, `''` when the id is missing, `undefined` off the edit route. */
function editFormIdFromPath(pathname: string): string | undefined {
  if (!pathname.includes(EDIT_FORM_PATH)) {
    return undefined;
  }
  return pathname.split('/').pop() ?? '';
}

const AppContent: React.FC = () => {
  const [showVerification, setShowVerification] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);
  const [inputPhoneOrEmail, setInputPhoneOrEmail] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [formsMetadata, setFormsMetadata] = useState<FormMetadata[] | null>(null);
  const [allQuestions, setAllQuestions] = useState<Question[]>([]);
  const [formTemplate, setFormTemplate] = useState<FormTemplate>({} as FormTemplate);
  const [stringsByLang, setStringsByLang] = useState<Record<string, StringsFile>>({});
  const [loadedStringsByLang, setLoadedStringsByLang] = useState<Record<string, StringsFile>>({});
  const [failedLangs, setFailedLangs] = useState<string[]>([]);
  const [loadingForm, setLoadingForm] = useState(false);
  const [verificationError, setVerificationError] = useState('');

  const { language, changeLanguage } = useLanguage();
  const translation = getTranslation(language);
  const location = useLocation();
  const navigate = useNavigate();

  const logout = useCallback(() => {
    setInputPhoneOrEmail('');
    setShowVerification(false);
    setSessionId('');
    setFormsMetadata(null);

    navigate(`/login`);
  }, [navigate]);

  const fetchQuestionsInBackground = async () => {
    try {
      const response = await getAllQuestions(translation, logout);
      setAllQuestions(response.questions as Question[]);
    } catch (error) {
      logClientError('fetchQuestionsInBackground', error);
    }
  };

  useEffect(() => {
    switch (language) {
      case Languages.ENGLISH:
        document.documentElement.lang = 'en';
        break;
      case Languages.ARABIC:
        document.documentElement.lang = 'ar';
        break;
      case Languages.RUSSIAN:
        document.documentElement.lang = 'ru';
        break;
      case Languages.HEBREW:
      default:
        document.documentElement.lang = 'he';
        break;
    }
  }, [language]);

  useEffect(() => {
    // A newer route supersedes this run; its late results must not overwrite the newer form.
    let cancelled = false;

    const initializeSession = async () => {
      try {
        setLoadingForm(true);
        setLoadingPage(true);

        if (location.pathname === ALL_FORMS_PATH) {
          // Re-opening a form from the list must load it fresh, not show unsaved edits.
          setFormTemplate({} as FormTemplate);
          setStringsByLang({});
          setFailedLangs([]);
          const response = await getFormsMetadata(translation, logout);
          if (cancelled) return;
          setFormsMetadata(response.data as FormMetadata[]);
          navigate(`${ALL_FORMS_PATH}`);
        }

        const formId = editFormIdFromPath(location.pathname);
        if (formId !== undefined) {
          if (!formId) {
            logClientError('initializeSession.missingFormId');
            logout();
          } else if (String(formTemplate.form_id) !== formId) {
            setLoadingForm(true);
            // Save sends languages from formsMetadata (AppRoutes); without it a refresh
            // would save the form as Hebrew-only and drop its declared languages.
            let metadata = formsMetadata;
            if (metadata === null) {
              const response = await getFormsMetadata(translation, logout);
              if (cancelled) return;
              metadata = response.data as FormMetadata[];
              setFormsMetadata(metadata);
            }
            const declaredLangs = normalizeFormLanguageCodes(
              metadata.find((form) => String(form.form_id) === String(formId))?.languages,
            );
            const loadResult = await loadSplitTemplateArtifacts(formId, declaredLangs, {
              getStructure: (id) => getFormStructure(id, translation, logout),
              getStrings: (id, lang) => getFormStrings(id, lang, translation, logout),
              getLegacyMono: (id) => getFormTemplate(id, translation, logout),
            });
            if (cancelled) return;
            const loadedTemplate = mergedFormTemplateFromLoadResult(loadResult);
            if (String(loadedTemplate.form_id) !== formId) {
              logClientError('initializeSession.formIdMismatch', { formId });
              navigate(ALL_FORMS_PATH);
            } else {
              setFormTemplate(loadedTemplate);
              const translationLangs = declaredLangs.filter((lang) => lang !== DEFAULT_LANG);
              if ('questions' in loadResult) {
                // Legacy mono carries Hebrew only; treat translations as not loaded so the
                // editor neither shows tabs nor overwrites the stored files on save.
                setStringsByLang({});
                setLoadedStringsByLang({});
                setFailedLangs(translationLangs);
              } else {
                const loaded = Object.fromEntries(
                  translationLangs
                    .filter((lang) => !loadResult.failedLangs.includes(lang))
                    .map((lang) => [lang, loadResult.stringsByLang[lang] ?? {}]),
                );
                setStringsByLang(loaded);
                setLoadedStringsByLang(loaded);
                setFailedLangs(loadResult.failedLangs);
              }
            }
          }
        }
      } catch (error) {
        if (cancelled) return;
        logClientError('initializeSession', error);
        logout();
      } finally {
        if (!cancelled) {
          setLoadingForm(false);
          setLoadingPage(false);
        }
      }

      if (cancelled) return;
      if (!allQuestions || allQuestions.length === 0) {
        await fetchQuestionsInBackground();
      }
    };

    initializeSession();

    const savedLanguage = sessionStorage.getItem(LANGUAGE_ITEM_NAME);
    if (savedLanguage) {
      changeLanguage(savedLanguage as Languages);
    }

    return () => {
      cancelled = true;
    };
    // allQuestions/fetchQuestionsInBackground intentionally omitted: including them
    // re-runs session init after background fetch completes. formTemplate/formsMetadata
    // are omitted so editing the form does not re-run init and reload the template.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeLanguage, location.pathname, logout, navigate, translation]);

  const appDirection: React.CSSProperties = {
    direction: isRTL(language) ? 'rtl' : 'ltr',
  };

  // The route changes one render before the load effect runs; never hand the editor
  // a template that belongs to another form (or the empty initial one).
  const editFormId = editFormIdFromPath(location.pathname);
  const waitingForEditTemplate =
    editFormId !== undefined && String(formTemplate.form_id) !== editFormId;

  return (
    <div className='app-container' style={appDirection}>
      {loadingPage || waitingForEditTemplate ? (
        <div className='app-loading-overlay'>
          <div className='app-spinner'></div>
        </div>
      ) : (
        <>
          <ScrollToTop />
          <AppRoutes
            showVerification={showVerification}
            setShowVerification={setShowVerification}
            inputPhoneOrEmail={inputPhoneOrEmail}
            setInputPhoneOrEmail={setInputPhoneOrEmail}
            sessionId={sessionId}
            setSessionId={setSessionId}
            logout={logout}
            formsMetadata={formsMetadata}
            setFormsMetadata={setFormsMetadata}
            formTemplate={formTemplate}
            setFormTemplate={setFormTemplate}
            stringsByLang={stringsByLang}
            setStringsByLang={setStringsByLang}
            loadedStringsByLang={loadedStringsByLang}
            failedLangs={failedLangs}
            loadingForm={loadingForm}
            verificationError={verificationError}
            setVerificationError={setVerificationError}
            allQuestions={allQuestions}
            setAllQuestions={setAllQuestions}
          />
        </>
      )}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
